/**
 * Celular (docs/spec/session-clock.md): o relógio da sessão é a segunda linha do título da mesa, ao
 * lado do voltar. Para quem administra, tocar abre Pausar/Continuar, Encerrar (que pergunta antes) e
 * Ver as sessões; para os outros, tocar abre a página das Sessões. Começar fica no menu da mesa (☰).
 * Logo depois do fim, o toque leva às avaliações da sessão (o jogador e o mestre).
 */
import { useState } from 'react';
import { useT } from '../../i18n/index.js';
import { gameSessionTimes } from '../../shared.js';
import { useApp } from '../../store.js';
import { ContextMenu, type MenuAnchor } from '../../ui/ContextMenu.js';
import { Icon } from '../../ui/icons.js';
import { askConfirm } from '../../ui/prompt.js';
import { useIsRoomGm } from '../audio/MusicPlayerModule.js';
import { useRateLabel, useRoomClock, useSessionRules } from './ClockPill.js';
import { clockDetails, formatClock, formatDuration, pillView, playAnchor } from './clockModel.js';
import { useServerNow } from './useServerNow.js';
import './clock.css';

export function ClockLine() {
  const t = useT();
  const clock = useRoomClock();
  const enabled = useApp((s) => s.serverFeatures.gameSessions);
  const control = useApp((s) => s.controlGameSession);
  const openModule = useApp((s) => s.requestOpenModule);
  const openSessions = () => openModule('system/gameSessions');
  const focusSession = useApp((s) => s.focusGameSession);
  const roomId = useApp((s) => s.activeRoomId);
  const rules = useSessionRules();
  const admin = useIsRoomGm();
  const rateLabel = useRateLabel(admin);
  const view = pillView(clock, admin, enabled && rules.record && rules.chatClock);
  const session = view.kind === 'open' || view.kind === 'ended' ? view.session : null;
  const now = useServerNow(session?.status === 'running', 1000, session ? playAnchor(session) : 0);
  const [menu, setMenu] = useState<MenuAnchor | null>(null);

  if (view.kind === 'ended') {
    const summary = t('clock.ended', { n: view.session.number, played: formatDuration(t, view.session.playedMs) });
    const number = view.session.number;
    return (
      <button
        type="button"
        className="rd-mclock rd-mclock--ended"
        title={rateLabel ?? t('sessions.open')}
        aria-label={rateLabel ? `${summary}. ${rateLabel}` : undefined}
        onClick={() => (rateLabel && roomId ? focusSession(roomId, number) : openSessions())}
      >
        <Icon name="stop" size={10} strokeWidth={0} filled />
        <span className="rd-mclock__summary">{summary}</span>
        {rateLabel ? (
          <span className="rd-mclock__rate" aria-hidden="true">
            <Icon name="star" size={10} strokeWidth={2.2} />
            {t('clock.rate')}
          </span>
        ) : null}
      </button>
    );
  }
  if (view.kind !== 'open') return null;

  const { session: open, paused } = view;
  const { playedMs } = gameSessionTimes(open, now);
  const played = formatDuration(t, playedMs);
  const content = (
    <>
      <span className="rd-clock__dot" aria-hidden="true" />
      <span className="rd-clock__time" role="timer" aria-label={t(paused ? 'clock.timer.paused' : 'clock.timer', { n: open.number, played })}>
        {formatClock(playedMs)}
      </span>
      {paused ? <span className="rd-clock__label">{t('clock.paused')}</span> : null}
    </>
  );
  const state = paused ? 'rd-mclock--paused' : 'rd-mclock--running';
  if (!view.controls) {
    return (
      <button type="button" className={`rd-mclock ${state}`} aria-label={t('sessions.open')} onClick={openSessions}>
        {content}
      </button>
    );
  }

  const end = async () => {
    const ok = await askConfirm({ title: t('clock.end'), message: t('clock.end.confirm', { n: open.number, played }), action: t('clock.end.yes'), danger: true });
    if (ok) control('end');
  };
  return (
    <>
      <button type="button" className={`rd-mclock ${state}`} aria-haspopup="menu" aria-label={t('clock.menu')} onClick={(e) => setMenu({ x: e.clientX, y: e.clientY })}>
        {content}
      </button>
      {menu ? (
        <ContextMenu
          anchor={menu}
          label={t('clock.label')}
          entries={[
            { kind: 'title', label: clockDetails(t, open, now) },
            { label: t(paused ? 'clock.resume' : 'clock.pause'), icon: <Icon name={paused ? 'play' : 'pause'} size={16} strokeWidth={0} filled />, onSelect: () => void control(paused ? 'resume' : 'pause') },
            { label: t('sessions.open'), icon: <Icon name="clock" size={16} />, onSelect: openSessions },
            { label: t('clock.end.menu'), icon: <Icon name="stop" size={16} strokeWidth={0} filled />, danger: true, onSelect: () => void end() },
          ]}
          onClose={() => setMenu(null)}
        />
      ) : null}
    </>
  );
}
